import { ResolvedThemeMode } from '../providers/theme-provider';

// Single source of truth for staging's colour palette. Development pulls these
// from a design-token system (theme/tokens/{primitives,semantic}.ts) that staging
// never picked up - see providers/theme-provider.tsx for why. On development each
// entry is a `var(--color-*)` custom property that switches with [data-theme];
// staging has no such variable system, so these are hardcoded to development's
// dark-mode hex values - the only mode staging renders. Covers only the paths the
// ported Portfolio Performance / Options Flow components reference; extend here as
// a future port needs, rather than bringing the full token system over.
//
// Use dot access (`tokens.flow.buy`) where possible for compile safety; the flat
// DARK_TOKENS lookup below is DERIVED from this object for the dynamic
// getTokenValue(path) callers only.
export const tokens = {
  text: {
    primary: '#eaedf0',
    secondary: '#d1dce6',
    tertiary: '#98a8b3',
    muted: '#667885',
    // charts.* / *.chart* are resolved dynamically (venue colours, line palette,
    // SVG hex), so the whole referenced set must be present, not just dot sites.
    chartAxis: '#f2f5f8',
  },
  bg: {
    page: '#000d14',
    surface: '#1d2f39',
    surfaceDark: '#102028',
    widget: '#2872e21a',
    // dev: var(--common-bg-table-line); backtest-result-header skeleton bgcolor.
    tableLine: '#5b8fdf1a',
  },
  border: {
    default: '#344854',
    subtle: '#667885',
    chartGrid: '#002649',
  },
  charts: {
    lightBlue: '#2e90fa',
    oliveGreen: '#abbd75',
    orange: '#f3733f',
    pink: '#f589d5',
    purple: '#9e77ed',
    turquoise: '#26d9d9',
    yellow: '#faa638',
  },
  flow: {
    buy: '#3987e5',
    sell: '#e66767',
    accent: '#c98500',
  },
  scrollbar: {
    thumb: '#416077',
    track: '#002649',
  },
  status: {
    error: '#f04438',
    success: '#32d583',
    successBg: '#03482c',
    successBorder: '#8df3b6',
  },
} as const;

// Dotted-path lookup ('flow.buy' -> '#3987e5') derived by flattening `tokens`,
// for the dynamic getTokenValue(path) callers that can't use compile-safe dot
// access. Keeping it derived means the hexes live in exactly one place.
const DARK_TOKENS: Record<string, string> = Object.fromEntries(
  Object.entries(tokens).flatMap(([group, entries]) =>
    Object.entries(entries).map(([name, hex]) => [`${group}.${name}`, hex]),
  ),
);

export function getTokenValue(tokenPath: string, _mode?: ResolvedThemeMode): string {
  const value = DARK_TOKENS[tokenPath];
  if (value === undefined) {
    throw new Error(`Token path "${tokenPath}" not found (staging tokens stub)`);
  }
  return value;
}
